"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { compact, countBy, map, uniq } from "lodash-es";
import { useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "use-intl";

import { CollectionIcon, RemoveFromCartIcon, WishlistIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import type { FilterOption } from "@/components/ui/filter-select";
import { useFeaturePageCatalog } from "@/features/feature-page/feature-page-catalog-context";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import {
  trackWishlistAddFailed,
  trackWishlistAddStarted,
  trackWishlistItemAdded,
} from "@/lib/analytics";
import { analyticsFailureReason } from "@/lib/analyticsFailureReason";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { recordPriceAlertCreated } from "@/lib/recordPriceAlertCreated";
import { client, orpc } from "@/utils/orpc.client";
import type { FeaturePageRenderModel } from "@garimpa/api/featurePages/types";
import { formatCountry, getFlagUrl } from "@garimpa/lib/countries";

import {
  type CollectionSort,
  type CollectionStatusFilter,
  collectionStatusOptions,
} from "./collection-grid-options";
import { CollectionGridResults } from "./collection-grid-results";
import { CollectionGridToolbar } from "./collection-grid-toolbar";
import { CollectionPanelHeader } from "./collection-panel-header";
import { toFeaturePageAlbumGridItem } from "./feature-page-album-grid-item";
import { filterAndSortCollectionItems } from "./filter-and-sort-collection-items";
import { isFeaturePageAlbumEntity } from "./is-feature-page-album-entity";
import { normalizeCollectionSort } from "./normalize-collection-sort";
import { paginateCollectionItems } from "./paginate-collection-items";

type CollectionModel = NonNullable<FeaturePageRenderModel["collection"]>;

const collectionStatusIcons = {
  owned: CollectionIcon,
  wishlisted: WishlistIcon,
  missing: RemoveFromCartIcon,
} satisfies Record<Exclude<CollectionStatusFilter, "all">, AppIcon>;

export function CollectionPanel({ collection }: { collection: CollectionModel }) {
  const t = useTranslations("featureCollections");
  const tGenres = useTranslations("shared.genres");
  const tStyles = useTranslations("shared.styles");
  const locale = useLocale();
  const catalog = useFeaturePageCatalog();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useViewerQueryState();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<CollectionSort>(() =>
    normalizeCollectionSort(collection.presentation.defaultSort),
  );
  const [status, setStatus] = useState<CollectionStatusFilter>("all");
  const [artist, setArtist] = useState<string | null>(null);
  const [genre, setGenre] = useState<string | null>(null);
  const [style, setStyle] = useState<string | null>(null);
  const [artistCountry, setArtistCountry] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const gridTopRef = useRef<HTMLDivElement>(null);
  const pageSize = Math.min(Math.max(collection.presentation.pageSize ?? 36, 12), 60);
  const gridItems = useMemo(() => {
    return collection.members.flatMap((member, originalIndex) => {
      const entity = catalog.getEntityByRef(member.catalogRefId);
      if (!entity) return [];
      return [
        {
          entity,
          member,
          originalIndex,
          artistCountryCode: entity.artistCountryCode,
        },
      ];
    });
  }, [catalog, collection.members]);
  const artistFilterOptions = useMemo(() => {
    const values = compact(gridItems.map((item) => item.entity.subtitle));
    const counts = countBy(values);
    const collator = new Intl.Collator(locale, { sensitivity: "base" });
    return uniq(values)
      .sort(collator.compare)
      .map((value) => ({
        value,
        label: value,
        count: counts[value] ?? 0,
      }));
  }, [gridItems, locale]);
  const genreFilterOptions = useMemo(() => {
    const values = gridItems.flatMap((item) => item.entity.genres);
    const counts = countBy(values);
    const collator = new Intl.Collator(locale, { sensitivity: "base" });
    return uniq(values)
      .sort(collator.compare)
      .map((value) => {
        const key = value as Parameters<typeof tGenres>[0];
        const label = tGenres.has(key) ? tGenres(key) : formatCatalogTaxonomyLabel(value);
        return {
          value,
          label,
          count: counts[value] ?? 0,
        };
      });
  }, [gridItems, locale, tGenres]);
  const styleFilterOptions = useMemo(() => {
    const values = gridItems.flatMap((item) => item.entity.styles);
    const counts = countBy(values);
    const collator = new Intl.Collator(locale, { sensitivity: "base" });
    return uniq(values)
      .sort(collator.compare)
      .map((value) => {
        const key = value as Parameters<typeof tStyles>[0];
        const label = tStyles.has(key) ? tStyles(key) : formatCatalogTaxonomyLabel(value);
        return {
          value,
          label,
          count: counts[value] ?? 0,
        };
      });
  }, [gridItems, locale, tStyles]);
  const artistCountryFilterOptions = useMemo(() => {
    const values = compact(uniq(map(gridItems, "artistCountryCode")));
    const counts = countBy(map(gridItems, "artistCountryCode"));
    return values
      .map((value) => ({
        value,
        label: formatCountry(value, locale, { includeUnknown: true }),
        count: counts[value] ?? 0,
        iconUrl: getFlagUrl(value) ?? undefined,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, locale));
  }, [gridItems, locale]);
  const ownedCount = collection.members.filter((member) =>
    member.inLists.includes("collection"),
  ).length;
  const wishlistedCount = collection.members.filter((member) =>
    member.inLists.includes("wishlist"),
  ).length;
  const missingCount = collection.members.filter((member) => member.inLists.length === 0).length;
  const totalCount = collection.members.length;
  const displayedProgress = {
    total: totalCount,
    owned: ownedCount,
    wishlisted: wishlistedCount,
    missing: missingCount,
    percentage: totalCount === 0 ? 0 : Math.round((ownedCount / totalCount) * 100),
  };
  const statusFilterOptions: FilterOption[] = collectionStatusOptions
    .filter((value): value is Exclude<CollectionStatusFilter, "all"> => value !== "all")
    .map((value) => ({
      value,
      label: t(`grid.status.${value}`),
      count:
        value === "owned"
          ? gridItems.filter((item) => item.member.inLists.includes("collection")).length
          : value === "wishlisted"
            ? gridItems.filter((item) => item.member.inLists.includes("wishlist")).length
            : gridItems.filter((item) => item.member.inLists.length === 0).length,
      icon: collectionStatusIcons[value],
    }));
  const sortOptions: FilterOption[] = [
    { value: "rankingAsc", label: t("grid.sort.rankingAsc") },
    { value: "rankingDesc", label: t("grid.sort.rankingDesc") },
    { value: "titleAsc", label: t("grid.sort.titleAsc") },
    { value: "titleDesc", label: t("grid.sort.titleDesc") },
    { value: "artistAsc", label: t("grid.sort.artistAsc") },
    { value: "artistDesc", label: t("grid.sort.artistDesc") },
  ];
  const visibleItems = useMemo(
    () =>
      filterAndSortCollectionItems({
        artist,
        artistCountry,
        genre,
        items: gridItems,
        locale,
        query,
        sort,
        status,
        style,
      }),
    [artist, artistCountry, genre, gridItems, locale, query, sort, status, style],
  );
  const paginatedItems = useMemo(
    () => paginateCollectionItems({ items: visibleItems, page, pageSize }),
    [page, pageSize, visibleItems],
  );
  const pageAlbums = compact(
    paginatedItems.items.map((item) => {
      if (!isFeaturePageAlbumEntity(item.entity)) return null;
      const album = toFeaturePageAlbumGridItem({ entity: item.entity });
      if (!album) return null;

      return { ...album, collectionItem: item };
    }),
  );
  const missingItems = visibleItems.filter((item) => item.member.inLists.length === 0);
  const bulkReleaseIds = uniq(
    compact(missingItems.map((item) => item.member.wishlistTargetReleaseId)),
  );
  const bulkMutation = useMutation({
    mutationFn: () => client.lists.addWishlistItems({ releaseIds: bulkReleaseIds }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.featurePages.getRenderModel.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.lists.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.music.key() }),
      ]);
      trackWishlistItemAdded("release", "feature_collection", Math.min(bulkReleaseIds.length, 500));
      recordPriceAlertCreated({
        selectionScope: "multiple",
        surface: "feature_collection",
      });
    },
    onError: (error: Error) => {
      trackWishlistAddFailed("release", "feature_collection", analyticsFailureReason(error));
    },
  });
  const hasQueryFilter = query.length > 0;
  const hasStatusFilter = status !== "all";
  const hasArtistFilter = artist !== null;
  const hasGenreFilter = genre !== null;
  const hasStyleFilter = style !== null;
  const hasArtistCountryFilter = artistCountry !== null;
  const hasActiveFilters =
    hasQueryFilter ||
    hasStatusFilter ||
    hasArtistFilter ||
    hasGenreFilter ||
    hasStyleFilter ||
    hasArtistCountryFilter;
  const updateFilter = (action: () => void) => {
    action();
    setPage(1);
  };
  const clearFilters = () => {
    setQuery("");
    setStatus("all");
    setArtist(null);
    setGenre(null);
    setStyle(null);
    setArtistCountry(null);
    setPage(1);
  };
  const changePage = (nextPage: number) => {
    setPage(nextPage);
    requestAnimationFrame(() => {
      gridTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <section
      className="w-full border-y border-border/60 bg-card/30 py-10"
      aria-labelledby="collection-members-title"
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <CollectionPanelHeader
          bulkReleaseCount={bulkReleaseIds.length}
          canAddMissing={isAuthenticated}
          collection={collection}
          displayedProgress={displayedProgress}
          isAdding={bulkMutation.isPending}
          isAuthenticated={isAuthenticated}
          onAddMissing={() => {
            trackWishlistAddStarted("release", "feature_collection");
            bulkMutation.mutate();
          }}
        />
        <CollectionGridToolbar
          artist={artist}
          artistCountry={artistCountry}
          artistCountryOptions={artistCountryFilterOptions}
          artistOptions={artistFilterOptions}
          genre={genre}
          genreOptions={genreFilterOptions}
          hasActiveFilters={hasActiveFilters}
          isAuthenticated={isAuthenticated}
          onArtistChange={(value) => updateFilter(() => setArtist(value))}
          onArtistCountryChange={(value) => updateFilter(() => setArtistCountry(value))}
          onClearFilters={clearFilters}
          onGenreChange={(value) => updateFilter(() => setGenre(value))}
          onQueryChange={(value) => updateFilter(() => setQuery(value))}
          onSortChange={(value) => updateFilter(() => setSort(value))}
          onStatusChange={(value) => updateFilter(() => setStatus(value))}
          onStyleChange={(value) => updateFilter(() => setStyle(value))}
          query={query}
          resultCount={visibleItems.length}
          sort={sort}
          sortOptions={sortOptions}
          status={status}
          statusOptions={statusFilterOptions}
          style={style}
          styleOptions={styleFilterOptions}
          totalCount={gridItems.length}
        />
        <div ref={gridTopRef} className="scroll-mt-24">
          <CollectionGridResults
            albums={pageAlbums}
            collection={collection}
            currentPage={paginatedItems.currentPage}
            onClearFilters={clearFilters}
            onPageChange={changePage}
            pageCount={paginatedItems.pageCount}
          />
        </div>
      </div>
    </section>
  );
}
